// Copyright ©️ 2026 eVolve MEP, LLC

import {
  Button,
  Card,
  Divider,
  Flex,
  getThemeColor,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  ThemeIcon,
  useMantineTheme,
} from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { ElementTagProperties } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/ElementTagProperties';
import {
  useAutodeskExtensionStoreActions,
  useAutodeskExtensionStoreSnapshot,
} from 'components/Autodesk/useAutodeskExtensionStore';
import { isNotNil } from 'helpers/isNotNil';

/**
 * In-viewer panel for placing and managing element tags.
 *
 * Rendered via `useViewerPortals` into the main app React tree, so Mantine and other app
 * context providers are available here.
 */
export const ElementTagsPanelSection = () => {
  const { selection } = useAutodeskExtensionStoreSnapshot('apsViewer');
  const { persistence, isDirty, isSaving } = useAutodeskExtensionStoreSnapshot('elementTags');
  const { save, cancel } = useAutodeskExtensionStoreActions('elementTags');

  const canSave = isDirty && isNotNil(persistence) && !isSaving;

  const onSave = () => {
    if (!canSave) return;
    void save();
  };

  const onCancel = () => {
    if (!canSave) return;
    cancel();
  };

  const theme = useMantineTheme();

  return (
    <Stack pos="relative" my="md">
      <Divider />
      <LoadingOverlay visible={isSaving} />
      {selection.length > 0 ? (
        <ElementTagProperties />
      ) : (
        <Card
          bg={`${theme.primaryColor}.0`}
          withBorder
          style={{ borderColor: getThemeColor(theme.primaryColor, theme) }}
        >
          <Flex gap="sm" align="center">
            <ThemeIcon radius="xl" color={theme.primaryColor}>
              <EvolveIcon icon="Alert" />
            </ThemeIcon>
            <Text fz="sm" c={theme.primaryColor}>
              Select an element to create a new tag.
            </Text>
          </Flex>
        </Card>
      )}
      {isDirty && (
        <Stack gap="xs">
          <Divider />
          <Text c="dimmed" fz="xs">
            You have unsaved changes.
          </Text>
          <Group flex={1} justify="end">
            <Button onClick={onCancel} variant="light">
              Cancel
            </Button>
            <Button onClick={onSave} disabled={isSaving}>
              Save
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
};
